import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Eye, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function PostCard({ post }) {
  const { i18n } = useTranslation();
  const currentLang = i18n.language; // 'zh' 或 'en'

  // --- 适配静态 JSON 结构 ---
  // data.json 结构是 title: { zh: "...", en: "..." }
  const title = post.title?.[currentLang] || post.title_zh || post.title_en;
  const excerpt = post.excerpt?.[currentLang] || post.excerpt_zh || post.excerpt_en;
  const content = post.content?.[currentLang] || post.content_zh || post.content_en;
  
  // 适配 Category
  const categoryName = typeof post.category === 'object' 
    ? post.category?.[currentLang] 
    : (post.category || 'Vineyard');

  // --- 适配图片路径 ---
  const getImageUrl = (url) => {
    if (!url) return '/assets/covers/default.webp';
    // 如果路径已经是 /assets/ 开头（脚本导出的路径），直接返回
    if (url.startsWith('/assets')) return url;
    // 兼容旧路径或外部链接
    if (url.startsWith('http')) return url;
    return url; 
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Recently";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Recently";
    return date.toLocaleDateString(currentLang === 'zh' ? 'zh-CN' : 'en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const stripHtml = (html) => html ? html.replace(/<[^>]*>?/gm, '') : '';
  const machineDate = post.date || post.publish_date || post.created_at;

  return (
    <article 
      itemScope 
      itemType="https://schema.org/BlogPosting" 
      className="group bg-white rounded-[2.5rem] overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 flex flex-col h-full border border-olive/5 hover:-translate-y-2"
    >
      
      {/* IMAGE SECTION */}
      <div className="relative h-80 md:h-64 overflow-hidden bg-gray-100 shrink-0">
        <Link to={`/post/${post.slug}`} aria-hidden="true" tabIndex="-1">
          <img
            itemProp="image"
            src={getImageUrl(post.cover_image)}
            alt={post.cover_alt?.[currentLang] || title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            loading="lazy"
          />
        </Link>
        
        <div className="absolute top-5 left-5">
          <span itemProp="articleSection" className="bg-[#556B2F]/90 backdrop-blur-sm text-white text-[10px] uppercase tracking-[0.2em] px-4 py-1.5 rounded-full shadow-lg font-bold">
            {categoryName}
          </span>
        </div>
      </div>
      
      {/* CONTENT SECTION */}
      <div className="px-6 py-5 flex flex-col flex-grow bg-white">
        <div className="flex items-center gap-4 text-[9px] uppercase tracking-widest text-[#556B2F]/60 mb-2 font-bold">
          <time 
            itemProp="datePublished" 
            dateTime={machineDate} 
            className="flex items-center gap-1.5"
          >
            <Calendar size={12} strokeWidth={2} />
            {formatDate(machineDate)}
          </time>
          <span className="flex items-center gap-1.5">
            <Eye size={12} strokeWidth={2} />
            {post.view_count || 0} {currentLang === 'zh' ? '次阅读' : 'views'}
          </span>
        </div>
        
        <h3 itemProp="headline" className="text-xl md:text-2xl font-serif text-[#3C4128] italic mb-2 leading-tight">
          <Link 
            to={`/post/${post.slug}`} 
            className="hover:text-[#800020] transition-colors duration-300"
          >
            {title}
          </Link>
        </h3>
        
        <p itemProp="abstract" className="text-[#3C4128]/70 text-xs md:text-sm leading-relaxed mb-5 line-clamp-2 font-light">
          {excerpt 
            ? stripHtml(excerpt) 
            : (content ? stripHtml(content).substring(0, 85) + '...' : '...')
          }
        </p>
        
        <div className="mt-auto pt-4 border-t border-[#556B2F]/10">
          <Link
            to={`/post/${post.slug}`}
            aria-label={`${currentLang === 'zh' ? '阅读更多关于' : 'Read more about'} ${title}`}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] font-bold text-[#556B2F] hover:text-[#800020] group-hover:gap-4 transition-all duration-300"
          >
            {currentLang === 'zh' ? '阅读更多' : 'Read more'} <ArrowRight size={14} />
          </Link>
        </div>
      </div>
      
      {/* Metadata for AI Agents (SEO Schema) */}
      <meta itemProp="author" content="EuroWineTrip" />
      <meta itemProp="publisher" content="EuroWineTrip" />
    </article>
  );
}